import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { FALLBACK_LANGUAGE, STORAGE_KEY, SUPPORTED_CODES } from "./languages";

import ruCommon from "./locales/ru/common.json";
import ruLogin from "./locales/ru/login.json";
import ruDashboard from "./locales/ru/dashboard.json";
import ruQuestions from "./locales/ru/questions.json";
import ruCover from "./locales/ru/cover.json";
import ruPhotos from "./locales/ru/photos.json";
import ruOnboarding from "./locales/ru/onboarding.json";
import ruLanding from "./locales/ru/landing.json";
import ruCheckout from "./locales/ru/checkout.json";
import enCommon from "./locales/en/common.json";
import enLogin from "./locales/en/login.json";
import enDashboard from "./locales/en/dashboard.json";
import enQuestions from "./locales/en/questions.json";
import enCover from "./locales/en/cover.json";
import enPhotos from "./locales/en/photos.json";
import enOnboarding from "./locales/en/onboarding.json";
import enLanding from "./locales/en/landing.json";
import enCheckout from "./locales/en/checkout.json";
import ukCommon from "./locales/uk/common.json";
import ukLogin from "./locales/uk/login.json";
import ukDashboard from "./locales/uk/dashboard.json";
import ukQuestions from "./locales/uk/questions.json";
import ukCover from "./locales/uk/cover.json";
import ukPhotos from "./locales/uk/photos.json";
import ukOnboarding from "./locales/uk/onboarding.json";
import ukLanding from "./locales/uk/landing.json";
import ukCheckout from "./locales/uk/checkout.json";
import beCommon from "./locales/be/common.json";
import beLogin from "./locales/be/login.json";
import beDashboard from "./locales/be/dashboard.json";
import beQuestions from "./locales/be/questions.json";
import beCover from "./locales/be/cover.json";
import bePhotos from "./locales/be/photos.json";
import beOnboarding from "./locales/be/onboarding.json";
import beLanding from "./locales/be/landing.json";
import beCheckout from "./locales/be/checkout.json";
import trCommon from "./locales/tr/common.json";
import trLogin from "./locales/tr/login.json";
import trDashboard from "./locales/tr/dashboard.json";
import trQuestions from "./locales/tr/questions.json";
import trCover from "./locales/tr/cover.json";
import trPhotos from "./locales/tr/photos.json";
import trOnboarding from "./locales/tr/onboarding.json";
import trLanding from "./locales/tr/landing.json";
import trCheckout from "./locales/tr/checkout.json";
import azCommon from "./locales/az/common.json";
import azLogin from "./locales/az/login.json";
import azDashboard from "./locales/az/dashboard.json";
import azQuestions from "./locales/az/questions.json";
import azCover from "./locales/az/cover.json";
import azPhotos from "./locales/az/photos.json";
import azOnboarding from "./locales/az/onboarding.json";
import azLanding from "./locales/az/landing.json";
import azCheckout from "./locales/az/checkout.json";
import kkCommon from "./locales/kk/common.json";
import kkLogin from "./locales/kk/login.json";
import kkDashboard from "./locales/kk/dashboard.json";
import kkQuestions from "./locales/kk/questions.json";
import kkCover from "./locales/kk/cover.json";
import kkPhotos from "./locales/kk/photos.json";
import kkOnboarding from "./locales/kk/onboarding.json";
import kkLanding from "./locales/kk/landing.json";
import kkCheckout from "./locales/kk/checkout.json";
import kyCommon from "./locales/ky/common.json";
import kyLogin from "./locales/ky/login.json";
import kyDashboard from "./locales/ky/dashboard.json";
import kyQuestions from "./locales/ky/questions.json";
import kyCover from "./locales/ky/cover.json";
import kyPhotos from "./locales/ky/photos.json";
import kyOnboarding from "./locales/ky/onboarding.json";
import kyLanding from "./locales/ky/landing.json";
import kyCheckout from "./locales/ky/checkout.json";

/**
 * Add translations for new namespaces (e.g. `dashboard`) here:
 *  1. Create locales/<lang>/<namespace>.json for every supported language.
 *  2. Import and add to `resources` below under each lang.
 *  3. Use via `const { t } = useTranslation('<namespace>')`.
 */

const resources = {
  ru: { common: ruCommon, login: ruLogin, dashboard: ruDashboard, questions: ruQuestions, cover: ruCover, photos: ruPhotos, onboarding: ruOnboarding, landing: ruLanding, checkout: ruCheckout },
  en: { common: enCommon, login: enLogin, dashboard: enDashboard, questions: enQuestions, cover: enCover, photos: enPhotos, onboarding: enOnboarding, landing: enLanding, checkout: enCheckout },
  uk: { common: ukCommon, login: ukLogin, dashboard: ukDashboard, questions: ukQuestions, cover: ukCover, photos: ukPhotos, onboarding: ukOnboarding, landing: ukLanding, checkout: ukCheckout },
  be: { common: beCommon, login: beLogin, dashboard: beDashboard, questions: beQuestions, cover: beCover, photos: bePhotos, onboarding: beOnboarding, landing: beLanding, checkout: beCheckout },
  tr: { common: trCommon, login: trLogin, dashboard: trDashboard, questions: trQuestions, cover: trCover, photos: trPhotos, onboarding: trOnboarding, landing: trLanding, checkout: trCheckout },
  az: { common: azCommon, login: azLogin, dashboard: azDashboard, questions: azQuestions, cover: azCover, photos: azPhotos, onboarding: azOnboarding, landing: azLanding, checkout: azCheckout },
  kk: { common: kkCommon, login: kkLogin, dashboard: kkDashboard, questions: kkQuestions, cover: kkCover, photos: kkPhotos, onboarding: kkOnboarding, landing: kkLanding, checkout: kkCheckout },
  ky: { common: kyCommon, login: kyLogin, dashboard: kyDashboard, questions: kyQuestions, cover: kyCover, photos: kyPhotos, onboarding: kyOnboarding, landing: kyLanding, checkout: kyCheckout },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: SUPPORTED_CODES,
    nonExplicitSupportedLngs: true, // treat 'ru-RU' as 'ru'
    ns: ["common", "login", "dashboard", "questions", "cover", "photos", "onboarding", "landing", "checkout"],
    defaultNS: "common",
    returnNull: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

export default i18n;
